'use client';

import { useCallback } from 'react';
import { isNil } from 'lodash';
import { useSearchParams } from 'next/navigation';
import { MEDIA_STATE_DEFAULT } from '@frameio/graphql-client/client-schema/media-state/MediaState.state';
import {
  useGetMediaStateQuery,
  useSetMediaStateMutation,
} from '@frameio/graphql-client/generated';
import { useActiveWindowBreakpoint } from '@frameio/vapor';
import type { PanelLayout } from '../../contexts';
import { useDispatchAppState } from '../../contexts';
import { usePanelVisibilityHistory } from '../../contexts/AppStateContext/appStateSelectors';
import {
  usePanelLayoutActions,
  useVisiblePanel,
} from '../../contexts/PanelLayoutContext/PanelLayoutContext';

type InteractionType = 'details' | 'transcription';

type UseTranscriptionPanelInteractionsOpts = {
  layout?: PanelLayout;
};

type UseTranscriptionPanelInteractionsReturn = {
  isTranscriptionPanelOpen: boolean;
  handleTranscriptionPanelToggle: (interaction: InteractionType) => void;
};

export const useTranscriptionPanelInteractions = ({
  layout = 'viewer',
}: UseTranscriptionPanelInteractionsOpts = {}): UseTranscriptionPanelInteractionsReturn => {
  const dispatchAppState = useDispatchAppState();
  const panelVisibilityHistory = usePanelVisibilityHistory();
  const breakpoint = useActiveWindowBreakpoint();
  const isCommentsPanelOpen = useVisiblePanel(layout ?? null, 'details');
  const { togglePanel } = usePanelLayoutActions();

  const mediaStateIndex = 0;
  const mediaStateId = MEDIA_STATE_DEFAULT;
  const mediaStates = useGetMediaStateQuery({
    variables: {
      mediaStateId,
    },
  });
  const [setMediaState] = useSetMediaStateMutation();

  const searchParams = useSearchParams();
  const comparisonAssetIds =
    searchParams
      ?.getAll?.('compare')
      ?.filter((id) => !isNil(id))
      .slice(0, 2) ?? [];
  const isComparisonViewer = comparisonAssetIds.length > 1;

  const isTranscriptionPanelOpen =
    mediaStates?.data?.getMediaState?.[mediaStateIndex]
      ?.isTranscriptionPanelOpen;

  const updatePanelHistory = useCallback(
    (history: {
      wasTranscriptionPanelForcedClosed: boolean;
      wasCommentsPanelForcedClosed: boolean;
    }) => {
      dispatchAppState({
        type: 'SET_PANEL_VISIBILITY_HISTORY',
        payload: history,
      });
    },
    [dispatchAppState]
  );

  const setTranscriptionPanelState = useCallback(
    (isOpen: boolean) => {
      setMediaState({
        variables: {
          mediaStateId: MEDIA_STATE_DEFAULT,
          mediaStateIndex,
          mediaState: {
            isTranscriptionPanelOpen: isOpen,
          },
        },
      });
    },
    [mediaStateIndex, setMediaState]
  );

  const handleTranscriptionPanelToggle = useCallback(
    (interaction: InteractionType) => {
      // Only handle interactions on mobile
      if (breakpoint !== 'base') return;

      // Don't handle details panel interactions in comparison view
      if (interaction === 'details' && isComparisonViewer) return;

      if (interaction === 'details') {
        if (isTranscriptionPanelOpen) {
          // Close transcription panel, remember state
          updatePanelHistory({
            wasTranscriptionPanelForcedClosed: true,
            wasCommentsPanelForcedClosed: false,
          });
          setTranscriptionPanelState(false);
          return;
        }

        if (panelVisibilityHistory.wasTranscriptionPanelForcedClosed) {
          // Restore transcription panel
          setTranscriptionPanelState(true);
          updatePanelHistory({
            wasTranscriptionPanelForcedClosed: false,
            wasCommentsPanelForcedClosed: false,
          });
        }
        return;
      }

      // Handling transcription panel toggle
      if (
        isTranscriptionPanelOpen &&
        panelVisibilityHistory.wasCommentsPanelForcedClosed
      ) {
        // Closing transcription panel, restore comments if needed
        togglePanel(layout, 'details');
        updatePanelHistory({
          wasCommentsPanelForcedClosed: false,
          wasTranscriptionPanelForcedClosed: false,
        });
        return;
      }

      if (!isTranscriptionPanelOpen) {
        // Opening transcription panel
        updatePanelHistory({
          wasCommentsPanelForcedClosed: isCommentsPanelOpen,
          wasTranscriptionPanelForcedClosed: isTranscriptionPanelOpen ?? false,
        });
        if (isCommentsPanelOpen) {
          togglePanel(layout, 'details');
        }
      }
    },
    [
      breakpoint,
      isComparisonViewer,
      isTranscriptionPanelOpen,
      panelVisibilityHistory.wasTranscriptionPanelForcedClosed,
      panelVisibilityHistory.wasCommentsPanelForcedClosed,
      setTranscriptionPanelState,
      updatePanelHistory,
      togglePanel,
      isCommentsPanelOpen,
      layout,
    ]
  );

  return {
    isTranscriptionPanelOpen: isTranscriptionPanelOpen ?? false,
    handleTranscriptionPanelToggle,
  };
};
